import React from 'react'
import { Table, Button, Tag, Space, Avatar, Dropdown } from 'antd'
import { MoreOutlined, EditOutlined, StopOutlined, CheckCircleOutlined, DeleteOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import type { User } from '../types/user.types'
import { DocumentAvatar } from '../../../shared/components/DocumentAvatar'

interface UsersTableProps {
    users: User[]
    onEdit: (user: User) => void
    onSuspend: (user: User) => void
    onDelete: (user: User) => void
    selectedRowKeys?: React.Key[]
    onSelectionChange?: (selectedRowKeys: React.Key[]) => void
    canSuspend?: boolean
    canDelete?: boolean
    loading?: boolean
    pagination?: {
        current: number
        pageSize: number
        total: number
        onChange: (page: number, pageSize: number) => void
    }
}

export const UsersTable: React.FC<UsersTableProps> = ({
    users,
    onEdit,
    onSuspend,
    onDelete,
    selectedRowKeys,
    onSelectionChange,
    canSuspend = true,
    canDelete = true,
    loading = false,
    pagination,
}) => {
    const columns: ColumnsType<User> = [
        {
            title: 'Customer',
            dataIndex: 'full_name',
            key: 'full_name',
            render: (full_name: string, record) => (
                <Space>
                    {record.profile_image ? (
                        <DocumentAvatar documentId={record.profile_image} />
                    ) : (
                        <Avatar style={{ backgroundColor: '#1890ff' }}>{full_name.charAt(0)}</Avatar>
                    )}
                    <div>
                        <div style={{ fontWeight: 'bold' }}>{full_name}</div>
                        <div style={{ fontSize: '12px', color: '#888' }}>{record.email || 'No email'}</div>
                    </div>
                </Space>
            ),
            sorter: (a, b) => a.full_name.localeCompare(b.full_name),
        },
        {
            title: 'Phone',
            dataIndex: 'mobile',
            key: 'mobile',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => {
                const colors = {
                    active: 'green',
                    suspended: 'red',
                    blocked: 'orange',
                }
                return <Tag color={colors[status as keyof typeof colors]}>{status.toUpperCase()}</Tag>
            },
        },
        {
            title: 'Created',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (created_at: string) => new Date(created_at).toLocaleDateString(),
            sorter: (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => {
                const items = [
                    {
                        key: 'edit',
                        icon: <EditOutlined />,
                        label: 'Edit',
                        onClick: () => onEdit(record),
                    },
                    ...(canSuspend ? [{
                        key: 'suspend',
                        icon: record.status === 'suspended' ? <CheckCircleOutlined /> : <StopOutlined />,
                        label: record.status === 'suspended' ? 'Activate' : 'Suspend',
                        danger: record.status !== 'suspended',
                        onClick: () => onSuspend(record),
                    }] : []),
                    ...(canDelete ? [{
                        key: 'delete',
                        icon: <DeleteOutlined />,
                        label: 'Delete',
                        danger: true,
                        onClick: () => onDelete(record),
                    }] : [])
                ]

                return (
                    <Dropdown menu={{ items }}>
                        <Button type="text" icon={<MoreOutlined />} />
                    </Dropdown>
                )
            },
        },
    ]

    return (
        <Table
            loading={loading}
            columns={columns}
            dataSource={users}
            rowKey="user_id"
            rowSelection={
                onSelectionChange
                    ? {
                        selectedRowKeys,
                        onChange: onSelectionChange,
                    }
                    : undefined
            }
            pagination={pagination ? {
                ...pagination,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} customers`,
            } : {
                total: users.length,
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} customers`,
            }}
        />
    )
}
